import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Flame, Hand, Star, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Reveal, Overline } from "@/components/Reveal";
import { ProductCard } from "@/components/ProductCard";

const HERO = "https://static.prod-images.emergentagent.com/jobs/58fc8f05-e99b-4946-b4cb-e7f6df7eb0e5/images/17f56cc1783288e9e899cd71e175de00652322b80183799471a7e0e74524072e.jpeg";
const STORY = "https://static.prod-images.emergentagent.com/jobs/58fc8f05-e99b-4946-b4cb-e7f6df7eb0e5/images/89720b52b4413453c42517f6d6037d61c0bbafa7991fdd9f981b2f68dd361ff2.jpeg";
const LAO_IMG = "https://static.prod-images.emergentagent.com/jobs/58fc8f05-e99b-4946-b4cb-e7f6df7eb0e5/images/a5eee30f96281beb1f047d8c00e981a4381098d0770a1a71b5cb36d9ea9931ce.jpeg";
const SHERRIE_IMG = "https://static.prod-images.emergentagent.com/jobs/58fc8f05-e99b-4946-b4cb-e7f6df7eb0e5/images/de9e30308d65bce69718b1ecac46d0ca77cdd3208e372d62013e470f754befe2.jpeg";

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [services, setServices] = useState([]);
    const [testimonials, setTestimonials] = useState([]);

    useEffect(() => {
        api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4)));
        api.get("/services").then((r) => setServices(r.data.slice(0, 6)));
        api.get("/testimonials").then((r) => setTestimonials(r.data));
    }, []);

    return (
        <div>
            {/* HERO */}
            <section className="relative min-h-[92vh] flex items-center">
                <img src={HERO} alt="Candlelit altar" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
                <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="max-w-2xl">
                        <Overline>The Fourth Bluff · Memphis, Tennessee</Overline>
                        <h1 className="mt-5 font-serif text-5xl sm:text-7xl text-cream leading-[0.95]">
                            The river don't lie,<br />and blood don't bargain.
                        </h1>
                        <p className="mt-6 text-lg text-ash max-w-lg leading-relaxed">
                            Sacred Earthworks is an old-time conjure house and heavy spiritual defense outfit -
                            deep-soil root medicine of bone, living water, forged iron, and unyielding ancestral law.
                        </p>
                        <div className="mt-9 flex flex-wrap gap-4">
                            <Link to="/services" data-testid="hero-services-cta" className="bg-gold text-ink rounded-full px-8 py-3.5 font-medium hover:bg-goldbright transition-colors active:scale-95 glow-gold">
                                Request Custom Work
                            </Link>
                            <Link to="/shop" data-testid="hero-shop-cta" className="border border-gold/40 text-gold rounded-full px-8 py-3.5 hover:bg-gold/10 transition-colors">
                                Shop Supplies
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* BRAND STORY */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 grid md:grid-cols-2 gap-14 items-center">
                <Reveal>
                    <img src={STORY} alt="Sherrie dressing a candle" className="rounded-2xl border border-gold/15 w-full aspect-square object-cover" />
                </Reveal>
                <Reveal delay={0.15}>
                    <Overline>Deep-Soil Root Medicine</Overline>
                    <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-cream leading-tight">We don't deal in sweet-talking remedies.</h2>
                    <p className="mt-6 text-ash leading-relaxed">
                        True spiritual trouble does not happen on paper, and it cannot be washed away with cheap perfume.
                        When a family line is tangled by inherited poverty, chronic sickness, or malice buried at the crossroads,
                        it takes surgical, physical excavation.
                    </p>
                    <p className="mt-4 text-ash leading-relaxed">
                        We work the primal balance of the ground, the heavy hammer that breaks the iron chain, and the deep,
                        silent spring that washes the wound clean. We clear the field of rot, turn malicious fire back on its
                        owner, and seal the household so the bloodline can sleep, earn, and flourish.
                    </p>
                    <Link to="/about" className="inline-block mt-7 text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors">Read the house's story →</Link>
                </Reveal>
            </section>

            {/* FEATURED SERVICES */}
            <section className="bg-surface/40 border-y border-gold/10 py-24">
                <div className="max-w-7xl mx-auto px-5 sm:px-8">
                    <Reveal><div className="text-center mb-14"><Overline>What the House Offers</Overline><h2 className="mt-3 font-serif text-4xl sm:text-5xl text-cream">Conjure &amp; Spellwork</h2></div></Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((s, i) => (
                            <Reveal key={s.id} delay={i * 0.06}>
                                <Link to={`/services/${s.id}`} data-testid={`home-service-${s.id}`} className="block h-full p-7 rounded-2xl bg-surface border border-gold/10 hover:border-gold/40 hover:-translate-y-1 transition-all duration-300">
                                    <Flame className="w-7 h-7 text-gold mb-4" />
                                    <h3 className="font-serif text-2xl text-cream">{s.name}</h3>
                                    <p className="mt-3 text-sm text-ash line-clamp-3">{s.description}</p>
                                    <p className="mt-4 text-gold">From ${s.price.toFixed(0)}</p>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED PRODUCTS */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
                <Reveal><div className="flex items-end justify-between mb-12"><div><Overline>From the Shop</Overline><h2 className="mt-3 font-serif text-4xl sm:text-5xl text-cream">Hand-Fixed Supplies</h2></div><Link to="/shop" className="hidden sm:block text-gold hover:text-goldbright">View all →</Link></div></Reveal>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                    {featured.map((p, i) => <Reveal key={p.id} delay={i * 0.06}><ProductCard product={p} /></Reveal>)}
                </div>
            </section>

            {/* HOW IT WORKS */}
            <section className="bg-surface/40 border-y border-gold/10 py-24">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid sm:grid-cols-3 gap-10 text-center">
                    {[{ icon: Hand, t: "Tell the house your need", d: "Share your situation through a booking or reading." },
                      { icon: Flame, t: "The work is set", d: "Iron, sulfur, and living water are laid, and lights are set on your behalf." },
                      { icon: Sparkles, t: "The ground is made clean", d: "Rot is cleared, fire is turned back, and your household is sealed." }].map((x, i) => (
                        <Reveal key={i} delay={i * 0.1}>
                            <div className="w-14 h-14 rounded-full border border-gold/30 flex items-center justify-center mx-auto mb-5"><x.icon className="w-6 h-6 text-gold" /></div>
                            <h3 className="font-serif text-2xl text-cream">{x.t}</h3>
                            <p className="mt-2 text-ash text-sm max-w-xs mx-auto">{x.d}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* FOUNDERS */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
                <Reveal><div className="text-center mb-14"><Overline>The Guardians of the Fourth Bluff</Overline><h2 className="mt-3 font-serif text-4xl sm:text-5xl text-cream">The Sledge &amp; The Wellspring</h2></div></Reveal>
                <div className="grid md:grid-cols-2 gap-6">
                    {[{ img: LAO_IMG, tag: "The Father · The Sun", name: "Lao, The Breaker & The Anvil", d: "The immovable wall between his family and the dark. He goes to the foundation with the wedge, the sulfur, and the cold iron, cutting the root and driving the boundary pins deep." },
                      { img: SHERRIE_IMG, tag: "The Mother · The Moon", name: "Sherrie, The Deep Sight & Living Sanctum", d: "The clear, deep water of the house. Her sight lays bare the truth behind the ribs, and her restoration calls back scattered luck and seals the rooms where the babies sleep." }].map((f, i) => (
                        <Reveal key={i} delay={i * 0.08}>
                            <Link to="/about" data-testid={`founder-${i}`} className="group flex flex-col sm:flex-row gap-6 h-full p-6 rounded-2xl bg-surface border border-gold/10 hover:border-gold/40 transition-colors">
                                <img src={f.img} alt={f.name} className="w-full sm:w-40 h-56 sm:h-52 object-cover object-top rounded-xl border border-gold/15" />
                                <div className="flex flex-col flex-1">
                                    <div className="overline-label">{f.tag}</div>
                                    <h3 className="mt-1 font-serif text-2xl text-cream">{f.name}</h3>
                                    <p className="mt-3 text-sm text-ash leading-relaxed flex-1">{f.d}</p>
                                    <span className="inline-flex items-center gap-1 mt-4 text-sm text-gold">Read their full story <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
                <Reveal><div className="text-center mb-14"><Overline>Word of Mouth</Overline><h2 className="mt-3 font-serif text-4xl sm:text-5xl text-cream">Those Who've Been Helped</h2></div></Reveal>
                <div className="grid md:grid-cols-2 gap-6">
                    {testimonials.map((t, i) => (
                        <Reveal key={t.id} delay={i * 0.06}>
                            <div className="p-8 rounded-2xl bg-surface border border-gold/10 h-full">
                                <div className="flex gap-1 mb-4">{Array.from({ length: t.rating }).map((_, j) => <Star key={j} className="w-4 h-4 fill-gold text-gold" />)}</div>
                                <p className="text-cream/90 font-serif text-xl leading-relaxed italic">"{t.text}"</p>
                                <p className="mt-5 text-sm text-gold">{t.name}<span className="text-faded"> · {t.service}</span></p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>
        </div>
    );
}
